// Lib
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

// Components

import PopupPanel from '../../../../components/popupPanel/PopupPanel';
import BillingTierFree from './billingStatus/BillingTierFree';
import Button from '../../../../components/buttons/Button';
import Icon from '../../../../components/icons/Icon';

// Hooks

import usePrevious from '../../../../utils/react/usePrevious';

// Utils

import { openPopup, closePopup } from '../../../../components/popupPanel/popupActions';
import popupOpenSelector from '../../../../components/popupPanel/popupOpenSelector';
import { getMilanoteApplicationModeSelector } from '../../../../platform/platformSelector';

import {
    contentTotalCountSelector,
    contentTotalLimitSelector,
} from '../../../../user/elementCount/elementCountSelector';

import { getCurrentUser } from '../../../../user/currentUserSelector';
import { isSubscribed } from '../../../../user/userUtil';
import { isUnlimited } from '../../../../../common/users/contentLimit';
import { getIsModalOpen } from '../../../../components/modal/activeModalSelector';

// Actions
import { openSheet } from '../../../../mobile/structural/sheet/sheetActions';

// Constants

import {
    ELEMENT_COUNT_WARNING_START,
    ELEMENT_COUNT_WARNING_INTERVAL,
    ELEMENT_COUNT_LIMIT_POPUP,
} from '../../../../user/elementCount/elementCountConstants';
import { MilanoteApplicationMode } from '../../../../../common/platform/platformTypes';
import { SheetId } from '../../../../mobile/structural/sheet/sheetTypes';

import './BillingAlertPopup.scss';

/**
 * Only show the close button if not in the mobile view.
 */
const BillingAlertCloseButton = ({ isMobileAppMode, closeAlertPopup }) => {
    if (isMobileAppMode) return null;
    return (
        <div className="header">
            <Button onClickFn={closeAlertPopup} className="close-button">
                <Icon name="close-x" />
            </Button>
        </div>
    );
};

BillingAlertCloseButton.propTypes = {
    closeAlertPopup: PropTypes.func,
    isMobileAppMode: PropTypes.bool,
};

const isOverWarningThreshold = (contentTotalCount, contentTotalLimit) =>
    contentTotalCount >= contentTotalLimit - ELEMENT_COUNT_WARNING_START;

const countSelector = createSelector(
    contentTotalCountSelector,
    contentTotalLimitSelector,
    popupOpenSelector(ELEMENT_COUNT_LIMIT_POPUP),
    getIsModalOpen,
    getCurrentUser,
    getMilanoteApplicationModeSelector,
    (contentTotalCount, contentTotalLimit, popupOpen, modalOpen, currentUser, appMode) => ({
        contentTotalCount,
        contentTotalLimit,
        popupOpen,
        modalOpen,
        currentUser,
        isMobileAppMode: appMode === MilanoteApplicationMode.mobile,
    }),
);

const mapDispatchToProps = (dispatch) => ({
    // Keep popup open on all events
    openAlertPopup: () => dispatch(openPopup(ELEMENT_COUNT_LIMIT_POPUP, () => true)),
    closeAlertPopup: () => dispatch(closePopup(ELEMENT_COUNT_LIMIT_POPUP)),
    dispatchOpenBillingAlertSheet: () => dispatch(openSheet(SheetId.BillingAlertPopup)),
});

const BillingAlertPopup = (props) => {
    const {
        currentUser,
        contentTotalCount,
        contentTotalLimit,
        popupOpen,
        modalOpen,
        isMobileAppMode,

        openAlertPopup,
        closeAlertPopup,
        dispatchOpenBillingAlertSheet,
    } = props;

    const prevContentCount = usePrevious(contentTotalCount);

    useEffect(() => {
        // if popup is open, close it if currentUser is subscribed or they have unlimited content
        if (popupOpen && (isSubscribed(currentUser) || isUnlimited(contentTotalLimit))) {
            closeAlertPopup();
            return;
        }

        // don't open popup if it's already open, or count went down
        if (popupOpen || modalOpen || contentTotalCount <= prevContentCount) return;

        const oldOverThreshold = isOverWarningThreshold(prevContentCount, contentTotalLimit);
        const currentOverThreshold = isOverWarningThreshold(contentTotalCount, contentTotalLimit);

        const oldInterval = Math.floor(prevContentCount / ELEMENT_COUNT_WARNING_INTERVAL);
        const newInterval = Math.floor(contentTotalCount / ELEMENT_COUNT_WARNING_INTERVAL);

        const shouldShowPopup =
            currentOverThreshold &&
            // We've now gone over the warning threshold
            (!oldOverThreshold ||
                // OR: The new count has hit the interval
                contentTotalCount % ELEMENT_COUNT_WARNING_INTERVAL === 0 ||
                // OR: The new count has skipped the interval
                newInterval - oldInterval >= 1);

        // if you have too much content, open popup or sheet if in mobile view
        if (shouldShowPopup && isMobileAppMode) {
            dispatchOpenBillingAlertSheet();
            return;
        }

        if (shouldShowPopup) openAlertPopup();
    }, [contentTotalCount, popupOpen, modalOpen, currentUser, prevContentCount, isMobileAppMode]);

    return (
        <PopupPanel {...props} popupId={ELEMENT_COUNT_LIMIT_POPUP} className="BillingAlertPopup" stayOpen>
            <BillingAlertCloseButton {...props} />

            <BillingTierFree
                currentUser={currentUser}
                buttonClass="StyledButton primary"
                onUpgradeClick={closeAlertPopup}
            />
        </PopupPanel>
    );
};

BillingAlertPopup.propTypes = {
    contentTotalCount: PropTypes.number,
    contentTotalLimit: PropTypes.number,
    openAlertPopup: PropTypes.func,
    closeAlertPopup: PropTypes.func,
    dispatchOpenBillingAlertSheet: PropTypes.func,
    currentUser: PropTypes.object,
    popupOpen: PropTypes.bool,
    modalOpen: PropTypes.bool,
    isMobileAppMode: PropTypes.bool,
};

export default connect(countSelector, mapDispatchToProps)(BillingAlertPopup);
